import React, { useState, useEffect } from 'react'
import { Card, Input, Button, Table, message, Tag, Form, Modal, Typography } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import { backend } from '../../api'

const { TextArea } = Input
const { Title, Text, Paragraph } = Typography

const STATUS_MAP = {
  open: { color: 'blue', text: '待处理' },
  pending: { color: 'orange', text: '待回复' },
  replied: { color: 'green', text: '已回复' },
  closed: { color: 'default', text: '已关闭' }
}

export default function SupportTickets() {
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [form] = Form.useForm()
  const [detailOpen, setDetailOpen] = useState(false)
  const [currentTicket, setCurrentTicket] = useState(null)
  const [replyContent, setReplyContent] = useState('')
  const [replying, setReplying] = useState(false)

  const loadTickets = async () => {
    setLoading(true)
    try {
      const res = await backend.getMyTickets()
      let list = []
      if (Array.isArray(res.tickets)) list = res.tickets
      else if (Array.isArray(res)) list = res
      else list = res.tickets || res.data?.tickets || []
      setTickets(list)
    } catch (err) {
      message.error('获取工单列表失败: ' + (err.message || '未知错误'))
    } finally { setLoading(false) }
  }

  useEffect(() => { loadTickets() }, [])

  const handleSubmit = async () => {
    if (!title.trim()) { message.warning('请输入工单标题'); return }
    if (!content.trim()) { message.warning('请输入工单内容'); return }
    setSubmitting(true)
    try {
      const res = await backend.createTicket(title.trim(), content.trim())
      if (res.success || res.ticket) {
        message.success('工单提交成功，管理员会尽快回复')
        setTitle(''); setContent(''); form.resetFields(); loadTickets()
      } else { message.error(res.message || '提交失败') }
    } catch (err) {
      message.error('提交工单失败: ' + (err.message || '未知错误'))
    } finally { setSubmitting(false) }
  }

  const handleReply = async () => {
    if (!replyContent.trim()) { message.warning('请输入回复内容'); return }
    if (!currentTicket) return
    setReplying(true)
    try {
      const res = await backend.replyTicket(currentTicket.id, replyContent.trim())
      if (res.success) {
        message.success('回复已发送')
        setReplyContent('')
        loadTickets()
        setCurrentTicket(prev => prev ? { ...prev, replies: [...(prev.replies || []), { content: replyContent.trim(), from_admin: false, created_at: new Date().toISOString() }] } : null)
      } else { message.error(res.message || '回复失败') }
    } catch (e) { message.error('回复失败') }
    finally { setReplying(false) }
  }

  const openDetail = (record) => {
    setCurrentTicket(record)
    setDetailOpen(true)
  }

  const columns = [
    { title: '标题', dataIndex: 'title', key: 'title' },
    { title: '内容', dataIndex: 'content', key: 'content', ellipsis: true },
    {
      title: '状态', dataIndex: 'status', key: 'status',
      render: s => {
        const cfg = STATUS_MAP[s] || { color: 'default', text: s || '未知' }
        return <Tag color={cfg.color}>{cfg.text}</Tag>
      }
    },
    {
      title: '创建时间', dataIndex: 'created_at', key: 'created_at',
      render: v => v ? new Date(v).toLocaleString('zh-CN') : '-'
    },
    {
      title: '操作', key: 'actions',
      render: (_, record) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)}>查看</Button>
      )
    }
  ]

  return (
    <div>
      <Card title="提交工单" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical">
          <Form.Item label="标题">
            <Input placeholder="请输入工单标题" value={title} onChange={e => setTitle(e.target.value)} />
          </Form.Item>
          <Form.Item label="内容">
            <TextArea rows={4} placeholder="请描述您遇到的问题" value={content} onChange={e => setContent(e.target.value)} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" loading={submitting} onClick={handleSubmit}>提交工单</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="我的工单">
        <Table dataSource={tickets} columns={columns} loading={loading} rowKey="id" pagination={{ pageSize: 10 }} />
      </Card>

      <Modal title="工单详情" open={detailOpen} onCancel={() => setDetailOpen(false)}
        footer={currentTicket?.status !== 'closed' ? [
          <Button key="cancel" onClick={() => setDetailOpen(false)}>关闭</Button>
        ] : [<Button key="cancel" onClick={() => setDetailOpen(false)}>关闭</Button>]}
        width={600}
      >
        {currentTicket && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>标题：</Text><Text>{currentTicket.title}</Text>
              <Tag color={STATUS_MAP[currentTicket.status]?.color || 'default'} style={{ marginLeft: 8 }}>
                {STATUS_MAP[currentTicket.status]?.text || currentTicket.status}
              </Tag>
            </div>
            <Card size="small" style={{ marginBottom: 12, background: '#f6f8fa' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>我的描述</Text>
              <Paragraph style={{ marginBottom: 0, marginTop: 4 }}>{currentTicket.content}</Paragraph>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {currentTicket.created_at ? new Date(currentTicket.created_at).toLocaleString('zh-CN') : ''}
              </Text>
            </Card>

            {(currentTicket.replies || []).length > 0 ? (
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>回复记录</Text>
                {(currentTicket.replies || []).map((r, i) => (
                  <Card key={i} size="small" style={{ marginTop: 8, background: r.from_admin ? '#e6f7ff' : '#f6f8fa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <Tag color={r.from_admin ? 'blue' : 'default'}>{r.from_admin ? '管理员回复' : '我的回复'}</Tag>
                      <Text type="secondary" style={{ fontSize: 11 }}>{r.created_at ? new Date(r.created_at).toLocaleString('zh-CN') : ''}</Text>
                    </div>
                    <Paragraph style={{ marginBottom: 0 }}>{r.content}</Paragraph>
                  </Card>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <Text type="secondary">暂无回复，请耐心等待管理员处理</Text>
              </div>
            )}

            {currentTicket.status !== 'closed' && (
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>追加回复</Text>
                <TextArea rows={3} value={replyContent} onChange={e => setReplyContent(e.target.value)} placeholder="补充描述您的问题..." />
                <div style={{ marginTop: 8, textAlign: 'right' }}>
                  <Button type="primary" onClick={handleReply} loading={replying}>发送</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
